/**
 * DocumentDatabaseTable Component
 *
 * Displays a table of document databases with actions (view, edit, delete, grant access).
 */

import React from 'react';
import { CircleStackIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import type { DocumentDatabaseSanitized } from '../../../types/document-database.types';
import { formatRelativeTime } from '../../../lib/utils/format';

interface DocumentDatabaseTableProps {
  documentDatabases: DocumentDatabaseSanitized[];
  isLoading?: boolean;
  onView: (documentDB: DocumentDatabaseSanitized) => void;
  onEdit?: (documentDB: DocumentDatabaseSanitized) => void;
  onDelete?: (documentDB: DocumentDatabaseSanitized) => void;
  /** Permission to edit document databases */
  canEdit?: boolean;
  /** Permission to delete document databases */
  canDelete?: boolean;
}

/**
 * DocumentDatabaseTable component
 */
export const DocumentDatabaseTable: React.FC<DocumentDatabaseTableProps> = ({
  documentDatabases,
  isLoading,
  onView,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={5} />;
  }

  if (documentDatabases.length === 0) {
    return (
      <EmptyState
        icon={<CircleStackIcon className="mx-auto h-12 w-12" />}
        title="No document databases found"
        description="Get started by creating a new document database."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Code
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Description
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {documentDatabases.map((item) => (
            <tr key={item.documentDBId} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <CircleStackIcon className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {item.documentDBName}
                    </div>
                    {item.schemaVersion && (
                      <div className="text-xs text-gray-500">
                        v{item.schemaVersion}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <CopyableCode value={item.documentDBCode} />
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate">
                  {item.description || (
                    <span className="text-gray-400 italic">No description</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatRelativeTime(item.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onView(item)}
                    title="View Details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                  {canEdit && onEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(item)}
                      title="Edit DocumentDB"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && onDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(item)}
                      title="Delete DocumentDB"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
